import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiRefreshCw, FiEdit3 } from 'react-icons/fi';
import apiService from '../../services/api';
import RubricBuilder from './RubricBuilder';

const Container = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`;

const SelectContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const Select = styled.select`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 32px;

  &:hover {
    border-color: var(--primary-color);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.25rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: var(--background-tertiary);
    border-color: var(--border-dark);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 600px) {
    width: 100%;
    justify-content: center;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 0.875rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: rgba(244, 67, 54, 0.1);
  border-left: 3px solid var(--error-color);
`;

const LoadingMessage = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RubricPreview = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const PreviewItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.25rem 0;
  align-items: center;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-color);
    padding-bottom: 0.5rem;
    margin-bottom: 0.5rem;
  }
`;

/**
 * Component to select or create a grading rubric
 */
function RubricSelector({ courseId, assignmentId, onSelect, selectedRubricId, onRubricSelected, showEditButton, onRubricsLoaded }) {
  const [rubrics, setRubrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRubric, setSelectedRubric] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);

  // Fetch rubrics for course
  useEffect(() => {
    const fetchRubrics = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiService.request(`/rubrics/course/${courseId}`);
        if (response.success) {
          const loadedRubrics = response.data || [];
          setRubrics(loadedRubrics);
          onRubricsLoaded && onRubricsLoaded(loadedRubrics);
        } else {
          throw new Error(response.message || 'Failed to fetch rubrics');
        }
      } catch (err) {
        console.error('Error fetching rubrics:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch rubrics if courseId is valid and not 'all'
    if (courseId && courseId !== 'all') {
      fetchRubrics();
    } else {
      setRubrics([]);
      setSelectedRubric(null);
      setLoading(false);
      setError(null);
      onRubricsLoaded && onRubricsLoaded([]);
    }
  }, [courseId, onRubricsLoaded]);

  // Load selected rubric details
  useEffect(() => {
    const loadRubric = async (rubricId) => {
      try {
        const response = await apiService.request(`/rubrics/${rubricId}`);
        if (response.success) {
          setSelectedRubric(response.data);
        }
      } catch (err) {
        console.error('Error loading rubric:', err);
      }
    };

    if (selectedRubricId) {
      loadRubric(selectedRubricId);
    }
  }, [selectedRubricId]);

  const handleRubricChange = (e) => {
    const rubricId = parseInt(e.target.value);
    if (rubricId) {
      const rubric = rubrics.find(r => r.id === rubricId);
      setSelectedRubric(rubric);
      onSelect(rubricId, rubric);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const response = await apiService.request(`/rubrics/course/${courseId}`);
      if (response.success) {
        const loadedRubrics = response.data || [];
        setRubrics(loadedRubrics);
        onRubricsLoaded && onRubricsLoaded(loadedRubrics);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRubricSaved = (newRubric) => {
    // Add new rubric to list and close builder
    const updatedRubrics = [...rubrics, newRubric];
    setRubrics(updatedRubrics);
    onRubricsLoaded && onRubricsLoaded(updatedRubrics);
    setShowBuilder(false);

    // Auto-select the newly created rubric
    setSelectedRubric(newRubric);
    onSelect(newRubric.id, newRubric);
  };

  // List view for editing rubrics
  if (showEditButton) {
    return (
      <Container>
        {!courseId || courseId === 'all' ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <p style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Please select a course</p>
            <p style={{ fontSize: '0.875rem' }}>Select a specific course from the filter above to view and edit rubrics.</p>
          </div>
        ) : loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Loading rubrics...
          </div>
        ) : error ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--danger-color)' }}>
            {error}
          </div>
        ) : rubrics.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            No rubrics found for this course.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {rubrics.map(rubric => (
              <div key={rubric.id} style={{
                padding: '1rem',
                border: '1px solid var(--border-color)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong style={{ display: 'block' }}>{rubric.name}</strong>
                  {rubric.description && (
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0 0' }}>
                      {rubric.description}
                    </p>
                  )}
                </div>
                <Button
                  onClick={() => onRubricSelected && onRubricSelected(rubric)}
                  title="Edit this rubric"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FiEdit3 size={14} />
                  Edit
                </Button>
              </div>
            ))}
          </div>
        )}
      </Container>
    );
  }

  return (
    <Container>
      <Label htmlFor="rubric-select">Select Grading Rubric *</Label>
      <SelectContainer>
        <Select
          id="rubric-select"
          value={selectedRubricId || ''}
          onChange={handleRubricChange}
          disabled={loading}
        >
          <option value="">-- Choose a rubric --</option>
          {rubrics.map(rubric => (
            <option key={rubric.id} value={rubric.id}>
              {rubric.name} ({rubric.max_points} pts)
            </option>
          ))}
        </Select>

        <Button onClick={handleRefresh} disabled={loading} title="Refresh rubric list">
          <FiRefreshCw size={16} />
          {loading ? 'Loading...' : 'Refresh'}
        </Button>

        <Button
          onClick={() => setShowBuilder(true)}
          title="Create new rubric"
        >
          <FiPlus size={16} />
          New
        </Button>
      </SelectContainer>

      {loading && (
        <LoadingMessage>
          Loading rubrics...
        </LoadingMessage>
      )}

      {error && (
        <ErrorMessage>{error}</ErrorMessage>
      )}

      {selectedRubric && selectedRubric.categories && (
        <RubricPreview>
          <strong>{selectedRubric.name}</strong>
          <p style={{ marginTop: '0.5rem', marginBottom: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {selectedRubric.description}
          </p>
          <div style={{ marginTop: '0.75rem' }}>
            {selectedRubric.categories.map((category, idx) => (
              <PreviewItem key={idx}>
                <span>{category.name}</span>
                <span style={{ fontWeight: 600 }}>{category.weight}%</span>
              </PreviewItem>
            ))}
          </div>
        </RubricPreview>
      )}

      {showBuilder && (
        <RubricBuilder
          courseId={courseId}
          onSave={handleRubricSaved}
          onCancel={() => setShowBuilder(false)}
        />
      )}
    </Container>
  );
}

export default RubricSelector;
